import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton,
  FormLabel,
} from "@chakra-ui/react";
import { MdArrowForward, MdLocationOn, MdCheckCircle, MdInfo, MdAccessTime } from "react-icons/md";
import { useGoogleMaps } from "components/GoogleMapsLoader";
import omnidocImg from "assets/img/dashboards/OMNIDOC.png";
import DoctorDropdown from "./DoctorDropdown";
import NurseDropdown from "./NurseDropdown";

const AffectationMap = ({ 
  vehicles, 
  missionLocation, 
  selectedAmbulanceId, 
  onAmbulanceSelect,
  doctors,
  ambulanciers,
  fetchVehicleLocation,
  nurses,
  selectedDoctorId,
  selectedNurseId,
  onDoctorSelect,
  onNurseSelect,
  typeMission,
  typeAmbulance,
  containerHeight = "calc(100vh - 300px)"
}) => {
  const { loaded } = useGoogleMaps();
  const mapRef = useRef(null);
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [circles, setCircles] = useState([]);
  const [directionsRenderer, setDirectionsRenderer] = useState(null);
  const [ambulancesWithDistance, setAmbulancesWithDistance] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [vehicleLocations, setVehicleLocations] = useState({});
  const distanceMatrixCacheRef = useRef(null);
  const directionsCacheRef = useRef(null);

  const parseLocValue = (locValue) => {
    if (!locValue) return null;
    if (typeof locValue === "object" && locValue.lat != null && locValue.lng != null) {
      const lat = Number(locValue.lat);
      const lng = Number(locValue.lng);
      if (!Number.isNaN(lat) && !Number.isNaN(lng)) return { lat, lng };
      return null;
    }
    const [latStr, lngStr] = String(locValue).split(",");
    const lat = Number(latStr);
    const lng = Number(lngStr);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    return { lat, lng };
  };

  // Initialiser la carte
  useEffect(() => {
    if (loaded && mapRef.current && !map && missionLocation?.lat && missionLocation?.lng) {
      const newMap = new window.google.maps.Map(mapRef.current, {
        center: { lat: missionLocation.lat, lng: missionLocation.lng },
        zoom: 13,
        styles: [
          { "elementType": "geometry", "stylers": [{ "color": "#ebe3cd" }] },
          { "elementType": "labels.text.fill", "stylers": [{ "color": "#523735" }] },
          { "elementType": "labels.text.stroke", "stylers": [{ "color": "#f5f1e6" }] },
          { "featureType": "administrative", "elementType": "geometry.stroke", "stylers": [{ "color": "#c9b2a6" }] },
          { "featureType": "administrative.land_parcel", "elementType": "geometry.stroke", "stylers": [{ "color": "#dcd2be" }] },
          { "featureType": "administrative.land_parcel", "elementType": "labels.text.fill", "stylers": [{ "color": "#ae9e90" }] },
          { "featureType": "landscape.natural", "elementType": "geometry", "stylers": [{ "color": "#dfd2ae" }] },
          { "featureType": "poi", "elementType": "geometry", "stylers": [{ "color": "#dfd2ae" }] },
          { "featureType": "poi", "elementType": "labels.text.fill", "stylers": [{ "color": "#93817c" }] },
          { "featureType": "poi.park", "elementType": "geometry.fill", "stylers": [{ "color": "#006133" }] },
          { "featureType": "poi.park", "elementType": "labels.text.fill", "stylers": [{ "color": "#447530" }] },
          { "featureType": "road", "elementType": "geometry", "stylers": [{ "color": "#f5f1e6" }] },
          { "featureType": "road.arterial", "elementType": "geometry", "stylers": [{ "color": "#fdfcf8" }] },
          { "featureType": "road.highway", "elementType": "geometry", "stylers": [{ "color": "#f8c967" }] },
          { "featureType": "road.highway", "elementType": "geometry.stroke", "stylers": [{ "color": "#e9bc62" }] },
          { "featureType": "road.highway.controlled_access", "elementType": "geometry", "stylers": [{ "color": "#e98d58" }] },
          { "featureType": "road.highway.controlled_access", "elementType": "geometry.stroke", "stylers": [{ "color": "#db8555" }] },
          { "featureType": "road.local", "elementType": "labels.text.fill", "stylers": [{ "color": "#806b63" }] },
          { "featureType": "transit.line", "elementType": "geometry", "stylers": [{ "color": "#dfd2ae" }] },
          { "featureType": "transit.line", "elementType": "labels.text.fill", "stylers": [{ "color": "#8f7d77" }] },
          { "featureType": "transit.line", "elementType": "labels.text.stroke", "stylers": [{ "color": "#ebe3cd" }] },
          { "featureType": "transit.station", "elementType": "geometry", "stylers": [{ "color": "#dfd2ae" }] },
          { "featureType": "water", "elementType": "geometry.fill", "stylers": [{ "color": "#b9d3c2" }] },
          { "featureType": "water", "elementType": "labels.text.fill", "stylers": [{ "color": "#92998d" }] }
        ],
      });
      setMap(newMap);
    }
  }, [loaded, missionLocation, map]);

  // Récupérer les positions des véhicules via leur tracker_id
  useEffect(() => {
    const fetchLocations = async () => {
      const locations = {};
      for (const vehicle of vehicles) {
        if (vehicle.tracker_id && !vehicleLocations[vehicle.id]) {
          const location = await fetchVehicleLocation(vehicle.tracker_id);
          if (location) {
            locations[vehicle.id] = location;
          }
        } else if (vehicle.lat != null && vehicle.lng != null) {
          locations[vehicle.id] = { lat: Number(vehicle.lat), lng: Number(vehicle.lng) };
        } else {
          const parsedLoc =
            parseLocValue(vehicle.loc) ||
            parseLocValue(vehicle.location) ||
            parseLocValue(vehicle.loc_ambulance) ||
            parseLocValue(vehicle.last_loc);
          if (parsedLoc) {
            locations[vehicle.id] = parsedLoc;
          }
        }
      }
      if (Object.keys(locations).length > 0) {
        setVehicleLocations(prev => ({ ...prev, ...locations }));
      }
    };

    if (vehicles.length > 0) {
      fetchLocations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicles]); // Retirer fetchVehicleLocation des dépendances pour éviter les boucles infinies

  // Calculer les distances et temps d'arrivée
  useEffect(() => {
    if (!map || !loaded || !vehicles.length || !missionLocation?.lat || Object.keys(vehicleLocations).length === 0) return;

    const service = new window.google.maps.DistanceMatrixService();
    const vehiclesWithLocation = vehicles.filter(v => vehicleLocations[v.id]);
    const origins = vehiclesWithLocation.map(v => vehicleLocations[v.id]);
    const destination = { lat: missionLocation.lat, lng: missionLocation.lng };

    if (origins.length === 0) return;

    // Utiliser une clé unique pour éviter les appels répétés
    const locationsKey = JSON.stringify(origins.map(o => `${o.lat},${o.lng}`).sort());
    const destinationKey = `${destination.lat},${destination.lng}`;
    const cacheKey = `${locationsKey}-${destinationKey}`;

    // Vérifier si on a déjà calculé pour ces positions
    if (distanceMatrixCacheRef.current === cacheKey) return;
    distanceMatrixCacheRef.current = cacheKey;

    service.getDistanceMatrix(
      {
        origins: origins,
        destinations: [destination],
        travelMode: window.google.maps.TravelMode.DRIVING,
        unitSystem: window.google.maps.UnitSystem.METRIC,
      },
      (response, status) => {
        if (status === "OK") {
          const ambulancesWithDist = vehiclesWithLocation
            .map((vehicle, index) => {
              const element = response.rows[index]?.elements[0];
              const distance = element?.distance?.value / 1000; // en km
              const duration = element?.duration?.value / 60; // en minutes

              return {
                ...vehicle,
                lat: vehicleLocations[vehicle.id].lat,
                lng: vehicleLocations[vehicle.id].lng,
                distance: distance ? distance.toFixed(1) : null,
                duration: duration ? Math.round(duration) : null,
              };
            })
            .sort((a, b) => {
              if (!a.distance) return 1;
              if (!b.distance) return -1;
              return parseFloat(a.distance) - parseFloat(b.distance);
            });

          setAmbulancesWithDistance(ambulancesWithDist);
        }
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, loaded, vehicles.length, missionLocation?.lat, missionLocation?.lng, Object.keys(vehicleLocations).length]);

  // Créer les marqueurs sur la carte
  useEffect(() => {
    if (!map || !loaded) return;

    // Nettoyer les anciens marqueurs et cercles
    markers.forEach(marker => marker.setMap(null));
    circles.forEach(circle => circle.setMap(null));

    const newMarkers = [];
    const newCircles = [];

    // Marqueur pour l'adresse de mission (rouge avec icône selon design Figma)
    if (missionLocation?.lat && missionLocation?.lng) {
      const svgMarker = `
        <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="14" cy="14" r="13.5" fill="rgba(248,40,90,0.2)" stroke="#F8285A" stroke-width="1"/>
          <circle cx="14" cy="14" r="8.33" fill="#F8285A"/>
          <circle cx="14" cy="14" r="4" fill="white" opacity="0.8"/>
        </svg>
      `;
      
      const iconUrl = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svgMarker);
      
      const missionMarker = new window.google.maps.Marker({
        position: { lat: missionLocation.lat, lng: missionLocation.lng },
        map: map,
        icon: {
          url: iconUrl,
          scaledSize: new window.google.maps.Size(28, 28),
          anchor: new window.google.maps.Point(14, 14),
        },
        title: "Patient",
        zIndex: 1000,
      });
      newMarkers.push(missionMarker);
    }

    // Marqueurs pour les ambulances
    ambulancesWithDistance.forEach((ambulance) => {
      const location = vehicleLocations[ambulance.id];
      if (location?.lat && location?.lng) {
        const isSelected = selectedAmbulanceId === ambulance.id;

        if (isSelected) {
          const svgMarkerSelected = `
            <svg width="85" height="85" viewBox="0 0 85 85" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="42.5" cy="42.5" r="42.5" fill="rgba(41,186,205,0.2)" stroke="rgba(41,186,205,0.2)" stroke-width="1"/>
              <circle cx="42.5" cy="42.5" r="32.5" fill="#29BACD" stroke="white" stroke-width="2"/>
            </svg>
          `;
          
          const iconUrlSelected = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svgMarkerSelected);
          
          const circleMarker = new window.google.maps.Marker({
            position: { lat: location.lat, lng: location.lng },
            map: map,
            icon: {
              url: iconUrlSelected,
              scaledSize: new window.google.maps.Size(85, 85),
              anchor: new window.google.maps.Point(42.5, 42.5),
            },
            title: ambulance.immatriculation || `AMB-${ambulance.id}`,
            zIndex: 1000,
          });
          newMarkers.push(circleMarker);
          
          const imageMarker = new window.google.maps.Marker({
            position: { lat: location.lat, lng: location.lng },
            map: map,
            icon: {
              url: omnidocImg,
              scaledSize: new window.google.maps.Size(50, 50),
              anchor: new window.google.maps.Point(25, 25),
            },
            title: ambulance.immatriculation || `AMB-${ambulance.id}`,
            zIndex: 1001,
          });
          newMarkers.push(imageMarker);
        } else {
          const ambulanceMarker = new window.google.maps.Marker({
            position: { lat: location.lat, lng: location.lng },
            map: map,
            icon: {
              url: omnidocImg,
              scaledSize: new window.google.maps.Size(40, 40),
              anchor: new window.google.maps.Point(20, 20),
            },
            title: ambulance.immatriculation || `AMB-${ambulance.id}`,
          });
          newMarkers.push(ambulanceMarker);
        }
      }
    });

    setMarkers(newMarkers);
    setCircles(newCircles);
  }, [map, loaded, ambulancesWithDistance, missionLocation, selectedAmbulanceId, vehicleLocations]);

  // Tracer le trajet pour l'ambulance sélectionnée
  useEffect(() => {
    if (!map || !loaded || !selectedAmbulanceId || !missionLocation?.lat) {
      if (directionsRenderer) {
        directionsRenderer.setMap(null);
        setDirectionsRenderer(null);
      }
      return;
    }

    const selectedAmbulance = ambulancesWithDistance.find(amb => amb.id === selectedAmbulanceId);
    if (!selectedAmbulance) {
      if (directionsRenderer) {
        directionsRenderer.setMap(null);
        setDirectionsRenderer(null);
      }
      return;
    }

    const location = vehicleLocations[selectedAmbulance.id];
    if (!location?.lat || !location?.lng) {
      if (directionsRenderer) {
        directionsRenderer.setMap(null);
        setDirectionsRenderer(null);
      }
      return;
    }

    const routeKey = `${selectedAmbulanceId}-${location.lat},${location.lng}-${missionLocation.lat},${missionLocation.lng}`;
    if (directionsCacheRef.current === routeKey) {
      return;
    }
    directionsCacheRef.current = routeKey;

    if (directionsRenderer) {
      directionsRenderer.setMap(null);
    }

    const directionsService = new window.google.maps.DirectionsService();
    const renderer = new window.google.maps.DirectionsRenderer({
      map: map,
      suppressMarkers: true,
      polylineOptions: {
        strokeColor: "#29BACD",
        strokeWeight: 4,
        strokeOpacity: 0.8,
      },
    });

    renderer._lastRouteKey = routeKey;
    setDirectionsRenderer(renderer);

    directionsService.route(
      {
        origin: { lat: location.lat, lng: location.lng },
        destination: { lat: missionLocation.lat, lng: missionLocation.lng },
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === "OK") {
          renderer.setDirections(result);
        } else {
          renderer.setMap(null);
          setDirectionsRenderer(null);
        }
      }
    );

    return () => {
      if (renderer) {
        renderer.setMap(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, loaded, selectedAmbulanceId, missionLocation?.lat, missionLocation?.lng, vehicleLocations[selectedAmbulanceId]?.lat, vehicleLocations[selectedAmbulanceId]?.lng]);

  // Focus sur l'ambulance sélectionnée
  useEffect(() => {
    if (!map || !loaded || !selectedAmbulanceId) return;

    const selectedAmbulance = ambulancesWithDistance.find(amb => amb.id === selectedAmbulanceId);
    if (selectedAmbulance) {
      const location = vehicleLocations[selectedAmbulance.id];
      if (location?.lat && location?.lng) {
        map.setCenter({ lat: location.lat, lng: location.lng });
        map.setZoom(15);
      }
    }
  }, [map, loaded, selectedAmbulanceId, ambulancesWithDistance, vehicleLocations]);

  const ambulancesForList = vehicles
    .map((vehicle) => {
      const withDistance = ambulancesWithDistance.find(
        (amb) => String(amb.id) === String(vehicle.id)
      );
      return withDistance ? { ...vehicle, ...withDistance } : { ...vehicle, distance: null, duration: null };
    })
    .sort((a, b) => {
      // Les ambulances avec distance connue passent en premier
      if (a.distance && !b.distance) return -1;
      if (!a.distance && b.distance) return 1;
      if (a.distance && b.distance) return parseFloat(a.distance) - parseFloat(b.distance);
      return 0;
    });

  const filteredAmbulances = ambulancesForList.filter((amb) =>
    amb.immatriculation?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    amb.id?.toString().includes(searchQuery)
  );

  const getAmbulancierForVehicle = (vehicle) => {
    return ambulanciers.find(a => a.id === vehicle.user_id) || null;
  };

  return (
    <Box position="relative" w="100%" h={containerHeight} minH="520px">
      {/* Carte Google Maps */}
      <Box
        ref={mapRef}
        w="100%"
        h="100%"
        borderRadius="10px"
        overflow="hidden"
      />

      {/* Panneau latéral */}
      <Box
        position="absolute"
        left="10px"
        top="10px"
        w="401px"
        h="calc(100% - 20px)"
        bg="white"
        border="1px solid"
        borderColor="#E5E5E5"
        borderRadius="12px"
        overflow="hidden"
        zIndex={10}
        display="flex"
        flexDirection="column"
      >
        {/* En-tête */}
        <Box
          bg="#FCFCFC"
          borderBottom="1px solid"
          borderColor="#E5E5E5"
          px="20px"
          py="14px"
        >
          <HStack justify="space-between" align="center">
            <Text fontSize="14px" fontWeight="500" color="#455879">
              Ambulance la plus proche
            </Text>
            <IconButton
              icon={<Icon as={MdArrowForward} transform="rotate(45deg)" />}
              size="sm"
              variant="ghost"
              aria-label="Fermer"
            />
          </HStack>
        </Box>

        {/* Barre de recherche */}
        <Box p="14px">
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <Icon as={MdInfo} color="#656C7A" />
            </InputLeftElement>
            <Input
              placeholder="Recherche d'une Ambulance..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              h="36px"
              borderRadius="6px"
              fontSize="13px"
            />
          </InputGroup>
        </Box>

        {/* Liste des ambulances */}
        <Box px="14px" pb="14px" flex="1" overflowY="auto">
          <VStack spacing="10px" align="stretch">
            {filteredAmbulances.map((ambulance, index) => {
              const isSelected = selectedAmbulanceId === ambulance.id;
              const isClosest = index === 0;
              const ambulancier = getAmbulancierForVehicle(ambulance);

              return (
                <Box
                  key={ambulance.id}
                  bg={isSelected ? "#EFF6FF" : "white"}
                  border="1px solid"
                  borderColor={isSelected ? "rgba(69,156,233,0.2)" : "#E5E5E5"}
                  borderRadius="6px"
                  p="12px"
                  cursor="pointer"
                  onClick={() => onAmbulanceSelect(ambulance.id)}
                  boxShadow="0px 2px 3px 0px rgba(0,0,0,0.04)"
                  _hover={{ borderColor: "#459CE9" }}
                >
                  <HStack justify="space-between" mb="5px">
                    <HStack spacing="10px">
                      <VStack align="flex-start" spacing="0">
                        <HStack spacing="5px" flexWrap="wrap">
                          <Text fontSize="14px" fontWeight="500" color={isSelected ? "#459CE9" : "#455879"}>
                            {ambulance.immatriculation || `AMB-${ambulance.id}`}
                          </Text>
                          {ambulance.type_vehicule && (
                            <Text fontSize="14px" fontWeight="400" color={isSelected ? "#459CE9" : "#656C7A"}>
                              - {ambulance.type_vehicule}
                            </Text>
                          )}
                          {ambulancier && (
                            <>
                              <Text fontSize="14px" fontWeight="400" color={isSelected ? "#459CE9" : "#656C7A"}>
                                - {ambulancier.nom} {ambulancier.prenom}
                              </Text>
                              {ambulance.date_achat && (
                                <Text fontSize="12px" fontWeight="400" color={isSelected ? "#459CE9" : "#656C7A"}>
                                  ({new Date(ambulance.date_achat).getFullYear()})
                                </Text>
                              )}
                            </>
                          )}
                          {isClosest && (
                            <Box
                              w="6px"
                              h="6px"
                              borderRadius="50%"
                              bg="#F8285A"
                              animation="pulse 2s infinite"
                            />
                          )}
                        </HStack>
                      </VStack>
                    </HStack>
                    {isSelected && (
                      <Box
                        bg="#459CE9"
                        borderRadius="6px"
                        p="9px"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Icon as={MdCheckCircle} w="16px" h="16px" color="white" />
                      </Box>
                    )}
                  </HStack>

                  <HStack spacing="5px" mt="5px">
                    <Box
                      border="1px solid"
                      borderColor={isSelected ? "#459CE9" : "#E5E5E5"}
                      borderRadius="4px"
                      px="8px"
                      py="8px"
                      bg={isSelected ? "transparent" : "#F9F9F9"}
                    >
                      <HStack spacing="5px">
                        <Icon as={MdLocationOn} w="14px" h="14px" color={isSelected ? "#459CE9" : "#455879"} />
                        <Text fontSize="14px" color={isSelected ? "#459CE9" : "#455879"}>
                          {ambulance.distance ? `${ambulance.distance} Km` : "--"}
                        </Text>
                      </HStack>
                    </Box>
                    <Box
                      border="1px solid"
                      borderColor={isSelected ? "#459CE9" : "#E5E5E5"}
                      borderRadius="4px"
                      px="8px"
                      py="8px"
                      bg={isSelected ? "transparent" : "#F9F9F9"}
                    >
                      <HStack spacing="5px">
                        <Icon as={MdAccessTime} w="14px" h="14px" color={isSelected ? "#459CE9" : "#455879"} />
                        <Text fontSize="14px" color={isSelected ? "#459CE9" : "#455879"}>
                          {ambulance.duration ? `${ambulance.duration} min` : "--"}
                        </Text>
                      </HStack>
                    </Box>
                  </HStack>

                  {/* Dropdowns Infirmier et Docteur */}
                  {isSelected && (
                    <VStack spacing="10px" mt="10px" align="stretch">
                      {/* Dropdown Infirmier en premier */}
                      <Box 
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                      >
                        <FormLabel fontSize="12px" fontWeight="500" color="#455879" mb="5px">
                          Infirmier
                        </FormLabel>
                        <NurseDropdown
                          value={selectedNurseId}
                          onChange={onNurseSelect}
                          options={nurses}
                          isRequired={false}
                        />
                      </Box>

                      {/* Dropdown Docteur ensuite */}
                      <Box 
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                      >
                        <FormLabel fontSize="12px" fontWeight="500" color="#455879" mb="5px">
                          Docteur
                        </FormLabel>
                        <DoctorDropdown
                          value={selectedDoctorId}
                          onChange={onDoctorSelect}
                          options={doctors}
                          isRequired={typeAmbulance === "TAR"}
                        />
                      </Box>
                    </VStack>
                  )}
                </Box>
              );
            })}
          </VStack>
        </Box>
      </Box>
    </Box>
  );
};

export default AffectationMap;
